import type { CalendarEvent } from "@tellescope/types-models";

export interface VideoIntegration {
  hasTwilioVideo: boolean;
  hasChimeVideo: boolean;
  hasZoomVideo: boolean;
  hasExternalVideo: boolean;
  hasHealthieZoom: boolean;
  hasAnyVideo: boolean;
  primaryVideoUrl?: string;
  videoType: "twilio" | "zoom" | "external" | "healthie" | null;
}

/**
 * Detect video integration type for a calendar event.
 * Ensures consistent video detection logic across all components.
 *
 * @param event - The calendar event to check for video integration
 * @returns Video integration details including type and availability
 *
 * @example
 * const videoIntegration = detectVideoIntegration(event);
 * if (videoIntegration.hasTwilioVideo) {
 *   // Show in-app video button
 * }
 */
export const detectVideoIntegration = (event: CalendarEvent): VideoIntegration => {
  const hasTwilioVideo = event.videoIntegration === "Twilio" && !!event.enableVideoCall;
  const hasChimeVideo = !!event.enableVideoCall && !event.videoIntegration;
  const hasZoomVideo = event.videoIntegration === "Zoom" && !!event.videoURL;
  const hasExternalVideo = !!event.externalVideoURL;
  const hasHealthieZoom = !!event.healthieZoomJoinURL;

  const hasAnyVideo = hasTwilioVideo || hasChimeVideo || hasZoomVideo || hasExternalVideo || hasHealthieZoom;

  const primaryVideoUrl = event.videoURL || event.externalVideoURL || event.healthieZoomJoinURL;

  let videoType: "twilio" | "zoom" | "external" | "healthie" | null = null;
  if (hasTwilioVideo || hasChimeVideo) videoType = "twilio";
  else if (hasZoomVideo) videoType = "zoom";
  else if (hasExternalVideo) videoType = "external";
  else if (hasHealthieZoom) videoType = "healthie";

  return {
    hasTwilioVideo,
    hasChimeVideo,
    hasZoomVideo,
    hasExternalVideo,
    hasHealthieZoom,
    hasAnyVideo,
    primaryVideoUrl,
    videoType,
  };
};
